{ Jadelet, Modal } from @danielx/dui

{round} := Math

// The mode switcher modal: pick the play mode (edit / practice / play), the
// view each mode uses (staff or the vertical roll), and the practice
// settings that used to live in the actions bar. Opened from the actions
// bar's mode button in edit mode and from the ⚙ gear while performing
// (views/mode-hud.civet). Practice and play rows stay purchase-gated like
// the 🎯/🎮 buttons they replace.
Template := Jadelet.exec """
  section.mode-modal
    h2 Mode
    button.close(click=@close) X
    @modeRows
    button.flat.collection(class=@hiddenUnlessPurchased click=@openCollection title="Built-in songs to practice and play") 🎵 Song Collection…
    section.midi-device(class=@hiddenUnlessPurchased)
      h3 MIDI Input
      select(value=@activeMidiDeviceIndex title="MIDI input device for practice, play, and recording")
        @midiDeviceOptions
    section.practice-settings(class=@practiceSettingsClass)
      h3 Practice Settings
      label.rate(title="Practice speed")
        span Speed
        input(type="range" min="50" max="100" step="5" value=@ratePercent)
        span.value @rateLabel
      label(title="Wait for note: the song holds at each of your notes until you play it")
        input(type="checkbox" checked=@wait)
        span Wait for note
      label(title=@loopTitle)
        button.flat(click=@cycleLoop) @loopLabel
      button.flat(click=@editSetup title="Choose the part you play") 🛠️ Song Practice Setup…
      button.flat(click=@myPractice title="Daily flash cards: note, chord, and scale recognition on your MIDI keyboard") 📚 My Practice…
"""

ModeRowTemplate := Jadelet.exec """
  .mode-row(@class)
    button.mode(@click class=@modeClass)
      span.icon @icon
      span @name
    .views
      button(click=@pickStaff class=@staffClass title="Sheet music") 🎼 Staff
      button(click=@pickRoll class=@rollClass title="Vertical roll: notes fall onto a keyboard") 🎹 Vertical
      button(click=@pickHRoll class=@hrollClass title="Horizontal roll: notes scroll across pitch lanes") 🎹 Horizontal
"""

OptionTemplate := Jadelet.exec """
option(@value) @name
"""

MODES := [
  { mode: "edit", icon: "✏️", name: "Edit" }
  { mode: "practice", icon: "🎯", name: "Practice" }
  { mode: "game", icon: "🎮", name: "Play" }
]

export ModeModal := (player: Player) ->
  player.showModeModal ?= ->
    Modal.show self.element

  modeRows := MODES.map ({mode, icon, name}) ->
    pickView := (view: string) -> ->
      player.setModeView mode, view

    ModeRowTemplate
      icon: icon
      name: name
      // edit is free; practice/play hide unless purchased, like 🎯/🎮 did
      class: -> player.hiddenUnlessPurchased() unless mode is "edit"
      click: ->
        // Hide first: entering a mode can itself open a modal (the practice
        // setup dialog, or the purchase prompt), and there is only one modal
        // — hiding afterwards would close the dialog we just opened.
        Modal.hide()
        player.enterMode mode as "edit" | "practice" | "game"
      modeClass: ->
        "active" if player.playMode() is mode
      pickStaff: pickView "staff"
      pickRoll: pickView "roll"
      pickHRoll: pickView "hroll"
      staffClass: ->
        "active" if player.modeView(mode) is "staff"
      rollClass: ->
        "active" if player.modeView(mode) is "roll"
      hrollClass: ->
        "active" if player.modeView(mode) is "hroll"

  self :=
    close: -> Modal.hide()
    modeRows: -> modeRows
    // One modal at a time: swap this one for the collection
    openCollection: ->
      Modal.hide()
      player.showSongCollection?()
    hiddenUnlessPurchased: player.hiddenUnlessPurchased

    // The device selector lives here with the rest of the performance
    // configuration (it used to hide behind a 🎹 button in the actions bar)
    activeMidiDeviceIndex: player.activeMidiDeviceIndex
    midiDeviceOptions: ->
      [
        OptionTemplate
          name: "None"
          value: -1
      ].concat player.midiDevices.map (input, i: number) ->
        OptionTemplate
          name: `${input.name} [${input.manufacturer}]`
          value: i

    // The practice aids are locked in game mode (rate is forced to 1, loop
    // and wait unavailable) — showing the controls there would offer a
    // slider whose only effect is resetting the run
    practiceSettingsClass: ->
      return "hidden" if player.playMode() is "game"
      player.hiddenUnlessPurchased()

    ratePercent: (...args: [] | [string]): string ->
      if args.length
        percent := parseInt args[0], 10
        if 50 <= percent <= 100
          player.practiceRate percent / 100
      String round player.practiceRate() * 100

    rateLabel: ->
      `${round player.practiceRate() * 100}%`

    wait: (...args: [] | [boolean]) ->
      if args.length
        player.waitForNote args[0]
      player.waitForNote()

    cycleLoop: player.cyclePracticeLoop

    loopLabel: ->
      if player.practiceLoop() then "Clear A/B loop ✕"
      else if player.practiceLoopStart()? then "Loop: set B at playhead"
      else "Loop: set A at playhead"

    loopTitle: ->
      if player.practiceLoop() then "Clear the practice loop"
      else if player.practiceLoopStart()? then "Loop end: press at bar B (hotkey l)"
      else "Loop start: press at bar A (hotkey l)"

    myPractice: ->
      player.showMyPractice?()

    editSetup: ->
      // Reopen the setup dialog to re-pick the part; it starts the target
      // mode on save. Prefer the mode we're in, practice from edit.
      m := player.playMode()
      player.practiceSetupTarget = if m is "game" then "game" else "practice"
      player.showPracticeSetup?()

    element: null as! HTMLElement

  self.element = Template self

  return self

export default ModeModal
